import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, Grid, InputLabel, MenuItem,
  Select, TextField, Typography, useTheme,
} from '@mui/material';
import { Add, Refresh } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';

import CustomPagination from '../../components/common/CustomPagination';
import {
  completeMarketplaceFulfillment,
  createMarketplaceFulfillment,
  getMarketplaceFulfillments,
  getMarketplacePackagingMaterials,
  getMarketplaceStorageAccounts,
} from '../../services/marketplaceWarehouse';

const initialForm = { storage_account_sid: '', order_reference: '', item_count: 1, package_count: 1, declared_value: 0, notes: '' };
const emptyMaterial = { packaging_material_id: '', quantity: 1 };

const MarketplaceFulfillmentPage = () => {
  const theme = useTheme();
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [materialLines, setMaterialLines] = useState([{ ...emptyMaterial }]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [fulfillmentResult, accountResult, materialResult] = await Promise.all([
        getMarketplaceFulfillments(), getMarketplaceStorageAccounts(), getMarketplacePackagingMaterials(),
      ]);
      setRows(fulfillmentResult.rows);
      setAccounts(accountResult.rows);
      setMaterials(materialResult.rows);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || requestError.message || 'Unable to load marketplace fulfillments.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const openComplete = (row) => {
    setSelected(row);
    setMaterialLines([{ ...emptyMaterial }]);
    setCompleteOpen(true);
  };

  const createFulfillment = async () => {
    setSaving(true);
    setError('');
    try {
      await createMarketplaceFulfillment({
        ...form,
        item_count: Number(form.item_count),
        package_count: Number(form.package_count),
        declared_value: Number(form.declared_value || 0),
      });
      setCreateOpen(false);
      setForm(initialForm);
      await loadData();
    } catch (requestError) {
      setError(JSON.stringify(requestError.response?.data || requestError.message));
    } finally {
      setSaving(false);
    }
  };

  const completeFulfillment = async () => {
    setSaving(true);
    setError('');
    try {
      await completeMarketplaceFulfillment(selected.sid, {
        materials: materialLines.map((line) => ({
          packaging_material_id: Number(line.packaging_material_id),
          quantity: Number(line.quantity),
        })),
      });
      setCompleteOpen(false);
      setSelected(null);
      await loadData();
    } catch (requestError) {
      setError(JSON.stringify(requestError.response?.data || requestError.message));
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { field: 'order_reference', headerName: 'Order Reference', width: 180 },
    { field: 'merchant_name', headerName: 'Merchant', flex: 1, minWidth: 190 },
    { field: 'warehouse_name', headerName: 'Warehouse', flex: 1, minWidth: 170 },
    { field: 'item_count', headerName: 'Items', width: 90 },
    { field: 'package_count', headerName: 'Packages', width: 100 },
    { field: 'status', headerName: 'Status', width: 120, renderCell: ({ value }) => <Chip size="small" label={value} color={value === 'completed' ? 'success' : 'warning'} /> },
    { field: 'merchant_fulfillment_amount', headerName: 'Fulfillment Fee', width: 145 },
    { field: 'packaging_material_amount', headerName: 'Packaging Materials', width: 170 },
    { field: 'actions', headerName: 'Actions', width: 140, sortable: false, renderCell: ({ row }) => row.status === 'draft' && <Button size="small" onClick={() => openComplete(row)}>Complete</Button> },
  ];
  const gridRows = useMemo(() => rows.map((row) => ({ ...row, id: row.sid })), [rows]);
  const totalPages = Math.ceil(gridRows.length / rowsPerPage);
  const paginatedRows = gridRows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const packagingTotal = materialLines.reduce((total, line) => {
    const material = materials.find((option) => option.id === Number(line.packaging_material_id));
    return total + (Number(material?.price_per_unit || 0) * Number(line.quantity || 0));
  }, 0);

  useEffect(() => { if (totalPages > 0 && page >= totalPages) setPage(totalPages - 1); }, [page, totalPages]);
  const handlePageChange = (nextPage) => { if (nextPage >= 0 && nextPage < totalPages) setPage(nextPage); };
  const handleRowsPerPageChange = (event) => {
    if (event.target.value === 'custom') { setCustomRowsActive(true); setCustomRowsInput(''); return; }
    setRowsPerPage(Number(event.target.value)); setPage(0);
  };
  const handleCustomRowsInputBlur = () => {
    if (Number(customRowsInput) > 0) setRowsPerPage(Number(customRowsInput));
    setCustomRowsActive(false); setCustomRowsInput(''); setPage(0);
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default', px: { xs: 0.75, sm: 1, md: 1.5 }, py: { xs: 2, sm: 2.5, md: 3 }, maxWidth: '95vw', overflowX: 'hidden' }}>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 7 }}><Typography variant="h4">Marketplace Fulfillment</Typography><Typography color="text.secondary">Record actual packing work and bill merchants with one fulfillment fee plus actual packaging materials.</Typography></Grid>
        <Grid size={{ xs: 12, md: 5 }}><Box display="flex" gap={1} flexWrap="wrap" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}><Button startIcon={<Refresh />} onClick={loadData}>Refresh</Button><Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>New Fulfillment</Button></Box></Grid>
      </Grid>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Card><CardContent><DataGrid autoHeight loading={loading} rows={paginatedRows} columns={columns} disableRowSelectionOnClick hideFooter sx={{ border: 'none', '& .MuiDataGrid-columnHeader': { backgroundColor: theme.palette.primary.main }, '& .MuiDataGrid-columnHeaderTitle': { color: theme.palette.primary.contrastText, fontWeight: 'bold' } }} /></CardContent></Card>
      <CustomPagination page={page} totalPages={totalPages} rowsPerPage={rowsPerPage} onPageChange={handlePageChange} onRowsPerPageChange={handleRowsPerPageChange} customRowsActive={customRowsActive} customRowsInput={customRowsInput} onCustomRowsInputChange={(event) => setCustomRowsInput(event.target.value.replace(/\D/g, ''))} onCustomRowsInputBlur={handleCustomRowsInputBlur} />

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth><DialogTitle>New Marketplace Fulfillment</DialogTitle><DialogContent><Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid size={{ xs: 12 }}><FormControl fullWidth required><InputLabel>Storage Account</InputLabel><Select label="Storage Account" value={form.storage_account_sid} onChange={(event) => setForm({ ...form, storage_account_sid: event.target.value })}>{accounts.map((account) => <MenuItem key={account.sid} value={account.sid}>{account.merchant_name} - {account.warehouse_name}</MenuItem>)}</Select></FormControl></Grid>
        <Grid size={{ xs: 12 }}><TextField fullWidth required label="Order Reference" value={form.order_reference} onChange={(event) => setForm({ ...form, order_reference: event.target.value })} /></Grid>
        <Grid size={{ xs: 6 }}><TextField fullWidth required type="number" label="Item Count" inputProps={{ min: 1 }} value={form.item_count} onChange={(event) => setForm({ ...form, item_count: event.target.value })} /></Grid>
        <Grid size={{ xs: 6 }}><TextField fullWidth required type="number" label="Package Count" inputProps={{ min: 1 }} value={form.package_count} onChange={(event) => setForm({ ...form, package_count: event.target.value })} /></Grid>
        <Grid size={{ xs: 12 }}><TextField fullWidth type="number" label="Declared Order Value" inputProps={{ min: 0, step: '0.01' }} value={form.declared_value} onChange={(event) => setForm({ ...form, declared_value: event.target.value })} /></Grid>
        <Grid size={{ xs: 12 }}><TextField fullWidth multiline minRows={2} label="Notes" value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} /></Grid>
      </Grid></DialogContent><DialogActions><Button onClick={() => setCreateOpen(false)}>Cancel</Button><Button variant="contained" disabled={saving || !form.storage_account_sid || !form.order_reference || Number(form.item_count) < 1 || Number(form.package_count) < 1} onClick={createFulfillment}>Create</Button></DialogActions></Dialog>

      <Dialog open={completeOpen} onClose={() => setCompleteOpen(false)} maxWidth="md" fullWidth><DialogTitle>Complete {selected?.order_reference}</DialogTitle><DialogContent><Typography color="text.secondary" sx={{ mb: 2 }}>Select each packaging material actually used. Current catalog prices are copied into the audit record.</Typography>
        {materialLines.map((line, index) => <Grid container spacing={2} key={index} sx={{ mb: 1.5 }}><Grid size={{ xs: 12, md: 8 }}><FormControl fullWidth required><InputLabel>Packaging Material</InputLabel><Select label="Packaging Material" value={line.packaging_material_id} onChange={(event) => setMaterialLines((current) => current.map((item, lineIndex) => lineIndex === index ? { ...item, packaging_material_id: event.target.value } : item))}>{materials.map((material) => <MenuItem key={material.id} value={material.id}>{material.name} - ₦{material.price_per_unit}</MenuItem>)}</Select></FormControl></Grid><Grid size={{ xs: 12, md: 4 }}><TextField fullWidth required type="number" label="Quantity Used" inputProps={{ min: 1 }} value={line.quantity} onChange={(event) => setMaterialLines((current) => current.map((item, lineIndex) => lineIndex === index ? { ...item, quantity: event.target.value } : item))} /></Grid></Grid>)}
        <Button onClick={() => setMaterialLines((current) => [...current, { ...emptyMaterial }])}>Add Material</Button><Typography sx={{ mt: 2, fontWeight: 700 }}>Packaging material total: ₦{packagingTotal.toFixed(2)}</Typography>
      </DialogContent><DialogActions><Button onClick={() => setCompleteOpen(false)}>Cancel</Button><Button variant="contained" disabled={saving || materialLines.some((line) => !line.packaging_material_id || Number(line.quantity) < 1)} onClick={completeFulfillment}>Complete and Bill</Button></DialogActions></Dialog>
    </Box>
  );
};

export default MarketplaceFulfillmentPage;
